/**
 * When to ask for the card (docs/PAYMENTS.md "When we ask"; rules in core payments.ts cardPromptDecision).
 * B = MAP: the prompt comes at the value moment, never before any value.
 * - At the Save (owner, 2026-10-10): a business with no card yet has each record held by the server (services/api
 *   heldentries.ts, 402 card_to_save); api.ts raises CARD_TO_SAVE_EVENT and the layout (components/Billing.tsx) opens
 *   "Almost there!" at once. This replaced "It's working." 8 seconds after a saved record: a record no longer saves without
 *   the card, so that moment is gone.
 * - Before: the first binder download, the first Insights, the first Health → the card first; "Not now" goes on.
 * Never on a check-in, a sick report or a food-safety step: those API calls are passed straight through.
 * The api wrapper only raises moments; the host (components/Billing.tsx, in the layout) decides with the core rule.
 */
import { NO_PROMPT_MEMORY, type CardPromptMemory, type CardPromptTrigger } from "@logwave/core";
import type { Api } from "../api.ts";

type BeforeTrigger = Exclude<CardPromptTrigger, "save">;

export interface CardPromptHost {
  orgId: string;
  /** The core rule, with this person's memory and today's date. */
  wants(trigger: BeforeTrigger): boolean;
  /** Before a value: show the prompt and resolve when the person goes on ("Not now" or closing). */
  before(trigger: BeforeTrigger): Promise<void>;
}

let host: CardPromptHost | null = null;
export function setCardPromptHost(h: CardPromptHost | null) { host = h; }
export function cardPromptHost() { return host; }

async function before(orgId: string, trigger: BeforeTrigger) {
  try { if (host && host.orgId === orgId && host.wants(trigger)) await host.before(trigger); } catch { /* never blocks the page */ }
}

/** The same api, with the before-value moments raised. Everything else (saves, check-ins, reports, steps) is untouched. */
export function withCardPrompts(api: Api): Api {
  return {
    ...api,
    binder: async (orgId, q) => { await before(orgId, "binder"); return api.binder(orgId, q); },
    analytics: async (orgId, q) => { await before(orgId, "insights"); return api.analytics(orgId, q); },
    orgHealth: async (orgId) => { await before(orgId, "health"); return api.orgHealth(orgId); },
  };
}

/* ─────────────── this browser's memory (per business, per person) ─────────────── */

const key = (orgId: string, who: string) => `cc.cardPrompt.${orgId}.${who}`;
export function loadPromptMemory(orgId: string, who: string): CardPromptMemory {
  try {
    const v = JSON.parse(localStorage.getItem(key(orgId, who)) ?? "null") as CardPromptMemory | null;
    return v && typeof v.notNow === "number" && Array.isArray(v.shown) ? v : NO_PROMPT_MEMORY;
  } catch { return NO_PROMPT_MEMORY; }
}
export function savePromptMemory(orgId: string, who: string, m: CardPromptMemory) {
  try { localStorage.setItem(key(orgId, who), JSON.stringify(m)); } catch { /* private mode: the prompt may repeat, never blocks */ }
}
